import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users, Clock, Coins, LogOut, TrendingDown, TrendingUp } from "lucide-react";
import Logo from "@/components/layout/Logo";
import CareTeamTabs from "@/components/layout/CareTeamTabs";

/**
 * Care Team → My Performance tab.
 *
 * Three sections, every one scoped to the single period picker at the
 * top so Deductions / Earnings / Net Commission always line up with the
 * Clients / Hours metrics on the same screen.
 *
 * The commission list is pulled from `staff_commission_ledger` on the
 * backend, server-filtered to the authenticated staff_id. The UI never
 * passes a staff_id — a tampered client can't surface another
 * practitioner's commission.
 */
const PERIOD_OPTIONS = [
  { value: "current_month", label: "Current Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_2_months", label: "Last 2 Months" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "last_4_months", label: "Last 4 Months" },
  { value: "last_5_months", label: "Last 5 Months" },
];

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

export default function MyPerformance() {
  const nav = useNavigate();
  const { staff, logoutStaff } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("current_month");
  const [data, setData] = useState(null);
  const [branchCurrency, setBranchCurrency] = useState("IDR");

  // Branches aren't on the staff response — pull once so we can show
  // commission amounts in the branch currency instead of a bare number.
  // Care-team staff live inside one brand; we take the first assigned
  // branch's currency as the display currency.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await staffApi.get("/branches");
        const first = Array.isArray(r.data) ? r.data[0] : null;
        if (mounted && first?.currency) setBranchCurrency(first.currency);
      } catch (_err) {
        // Silently fall back to IDR — commission math is still correct.
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const r = await staffApi.get("/staff/my-performance", { params: { period } });
        if (mounted) setData(r.data);
      } catch (err) {
        if (mounted) toast.error(formatApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [period]);

  const totals = data?.commission?.totals || { earned: 0, deducted: 0, net: 0 };
  const earned = data?.commission?.earned || [];
  const deductions = data?.commission?.deductions || [];
  const hasDeductions = deductions.length > 0;
  const money = useMemo(() => (n) => formatBranchMoney(branchCurrency, n), [branchCurrency]);

  return (
    <div className="min-h-screen bg-[hsl(40_20%_97%)] pb-20 sm:pb-6">
      <header className="sticky top-0 z-10 h-14 px-4 sm:px-6 border-b border-border bg-white/95 backdrop-blur flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="mark" to={null} size={32} testid="myperf-brand-logo" />
          <div className="hidden sm:block">
            <p className="text-[11px] tracking-overline text-muted-foreground">CARE TEAM DISPATCH</p>
            <p className="text-sm font-semibold leading-tight">My Performance</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground max-w-[8rem] truncate">{staff?.full_name}</span>
          <Button variant="ghost" size="sm" onClick={() => { logoutStaff(); nav("/staff/login"); }} data-testid="myperf-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="hidden sm:block max-w-2xl mx-auto pt-4 px-3 sm:px-6">
        <CareTeamTabs />
      </div>

      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6">
        <div className="mb-6 sm:hidden">
          <p className="text-[11px] tracking-overline text-muted-foreground">CARE TEAM DISPATCH</p>
          <h1 className="text-2xl font-semibold mt-1">My Performance</h1>
          <p className="text-xs text-muted-foreground mt-1">Snapshot of your work for the selected period.</p>
        </div>

        <div className="mb-5 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] tracking-overline text-muted-foreground">PERIOD</p>
            <h2 className="text-base font-semibold truncate" data-testid="myperf-month-label">
              {loading ? "Loading…" : (data?.month_label || "Current month")}
            </h2>
          </div>
          <div className="w-40 sm:w-48 shrink-0">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="myperf-period-select" aria-label="Select period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`myperf-period-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="myperf-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-24 rounded-xl bg-white border border-border" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="myperf-metrics">
              <div className="rounded-xl border border-border bg-white p-4 flex items-center gap-3" data-testid="myperf-total-clients">
                <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] tracking-overline text-muted-foreground">TOTAL CLIENTS SERVED</p>
                  <p className="text-2xl font-semibold tabular-nums leading-tight">{data?.total_clients ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">Unique daily visits</p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-white p-4 flex items-center gap-3" data-testid="myperf-treatment-hours">
                <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] tracking-overline text-muted-foreground">TREATMENT HOURS</p>
                  <p className="text-2xl font-semibold tabular-nums leading-tight">
                    {Number(data?.treatment_hours ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Closed appointments</p>
                </div>
              </div>
            </div>

            <CommissionSection
              hasDeductions={hasDeductions}
              deductions={deductions}
              earned={earned}
              totals={totals}
              money={money}
            />
          </>
        )}
      </div>

      <div className="sm:hidden">
        <CareTeamTabs />
      </div>
    </div>
  );
}

function CommissionSection({ hasDeductions, deductions, earned, totals, money }) {
  const nothing = !hasDeductions && earned.length === 0;
  return (
    <section
      className="rounded-xl border border-border bg-white overflow-hidden"
      data-testid="myperf-commission-section"
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center">
          <Coins className="w-4 h-4" />
        </div>
        <div>
          <p className="text-[11px] tracking-overline text-muted-foreground">COMMISSION</p>
          <p className="text-sm font-semibold leading-tight">Earnings &amp; deductions</p>
        </div>
      </header>

      {nothing ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="myperf-commission-empty">
          No commission activity for this period.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {hasDeductions && (
            <LedgerBlock
              label="Deductions"
              testid="myperf-commission-deductions"
              rows={deductions}
              tone="red"
              icon={<TrendingDown className="w-3.5 h-3.5" />}
              money={money}
            />
          )}
          <LedgerBlock
            label="Commission Earned"
            testid="myperf-commission-earnings"
            rows={earned}
            tone="green"
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            money={money}
            emptyMessage="No earnings recorded for this period."
          />
          <SummaryRow totals={totals} money={money} />
        </div>
      )}
    </section>
  );
}

function LedgerBlock({ label, testid, rows, tone, icon, money, emptyMessage }) {
  const tint = tone === "red"
    ? "text-rose-700 bg-rose-50/60 border-rose-100"
    : "text-emerald-700 bg-emerald-50/60 border-emerald-100";
  const chipTint = tone === "red" ? "text-rose-600" : "text-emerald-600";
  return (
    <div data-testid={testid}>
      <div className={`px-4 py-2 border-b border-dashed ${tint} flex items-center gap-2`}>
        <span className={`inline-flex items-center gap-1 ${chipTint}`}>{icon}</span>
        <p className="text-[11px] tracking-overline font-semibold">{label}</p>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">{emptyMessage || "No records."}</div>
      ) : (
        <ul className="divide-y divide-border/60">
          {rows.map((r) => (
            <li
              key={r.id}
              className="px-4 py-3 flex items-start gap-3"
              data-testid={`${testid}-row`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground tabular-nums">{formatDate(r.date)}</p>
                <p className="text-sm font-medium truncate" title={r.customer_name}>{r.customer_name}</p>
                <p className="text-xs text-muted-foreground truncate" title={r.treatment_name}>{r.treatment_name}</p>
                {r.branch_name ? (
                  <p
                    className="text-[11px] text-muted-foreground/80 mt-0.5 truncate"
                    data-testid={`${testid}-row-branch`}
                    title={r.branch_name}
                  >
                    <span className="inline-block px-1.5 py-0.5 rounded-full bg-muted text-[10px] font-medium tracking-wide uppercase">
                      {r.branch_name}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className={`text-sm font-semibold tabular-nums shrink-0 ${tone === "red" ? "text-rose-600" : "text-emerald-600"}`}>
                {tone === "red" ? "-" : "+"}{money(Math.abs(Number(r.amount) || 0))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SummaryRow({ totals, money }) {
  const earned = Number(totals.earned) || 0;
  const deducted = Number(totals.deducted) || 0;
  const net = Number(totals.net) || 0;
  return (
    <div className="bg-[hsl(40_20%_98%)] px-4 py-4" data-testid="myperf-commission-summary">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span>Total Earned</span>
        <span className="tabular-nums text-emerald-600 font-medium" data-testid="myperf-summary-earned">
          {money(earned)}
        </span>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
        <span>Total Deducted</span>
        <span className="tabular-nums text-rose-600 font-medium" data-testid="myperf-summary-deducted">
          -{money(deducted)}
        </span>
      </div>
      <div className="h-px bg-border mb-2" />
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">Net Commission</span>
        <span
          className={`text-lg font-semibold tabular-nums ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}
          data-testid="myperf-summary-net"
        >
          {money(net)}
        </span>
      </div>
    </div>
  );
}
